/**
 * ตรรกะล้วนของหน้าจอ Packing แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * สามข้อที่แพงที่สุดของกลุ่มนี้อยู่ที่นี่:
 *
 * 1. **ผลรวมจำนวน lot เทียบกับจำนวนของกล่อง** (`packingSumSummary`) — invariant
 *    ข้อ 3 ของ Work Order นี้บอกตรง ๆ ว่า "THE SUM RULE IS THE SCREEN'S WHOLE
 *    JOB": ไม่มี constraint ฐานข้อมูลใดถือกฎนี้ (D-105 ข้อ 1) หน้าจอจึงต้องโชว์
 *    ผลรวมและผลต่างให้เห็นสดขณะเพิ่ม lot คำนวณด้วยเลขจำนวนเต็ม scale คงที่
 *    (ทศนิยมสี่ตำแหน่ง ตาม `NUMERIC(18,4)`) **ไม่ใช้ `Number()`** เพราะ IEEE-754
 *    double เก็บทศนิยมสี่ตำแหน่งที่มีเลขจำนวนเต็มสิบสี่หลักไม่ครบ
 * 2. **แถวนี้มีปุ่มอะไรได้บ้าง** (`packingUnitLifecycleActionsFor`) อ่าน state
 *    machine ที่ `service.py` บังคับจริงออกมาเป็นคำตอบเดียว: `confirm` ต้องการ
 *    `PACKING` เท่านั้น `unpack` ต้องการ `PACKED` เท่านั้น — สี่สถานะที่เหลือ
 *    (`DRAFT`, `LOADED`, `DISPATCHED`, `RETURNED`) ไม่มีปุ่มบนหน้าจอนี้เลย
 * 3. **การประกอบ body ของ `POST /v1/packing-units`** (`toCreatePackingUnitBody`)
 *    ซึ่งเป็นด่านเดียวที่ทางเขียนของหน้าจอนี้เดินผ่าน — "lot ทั้งหมดต้องมาพร้อม
 *    หัวกล่องในคำขอเดียว" (ดูหมายเหตุใน `types.ts`) จึงถูกบังคับที่นี่ก่อนคำขอ
 *    จะออกเดินทาง
 *
 * ⚠️ ไฟล์นี้ไม่มีฟังก์ชันที่ประกอบ `status`, `packing_unit_id` หรือฟิลด์ผู้กระทำ
 * เลย — ทั้งหมดเป็นของเซิร์ฟเวอร์ สถานะเปลี่ยนได้ทางเดียวคือ transition
 */

import {
  DISPATCHED_PACKING_STATUS,
  DRAFT_PACKING_STATUS,
  LOADED_PACKING_STATUS,
  PACKED_PACKING_STATUS,
  PACKING_PACKING_STATUS,
  RETURNED_PACKING_STATUS,
  type CreatePackingUnitBody,
  type CreatePackingUnitLot,
  type PackingUnitSummary,
} from './types';
import type { PackingUnitTransition } from './client';

/* ------------------------------------------------------ เลขทศนิยมปลอดภัย -- */

/** `NUMERIC(18, 4)` — สี่ตำแหน่งหลังจุดเสมอ, IEEE-754 double เก็บไม่ครบ */
const DECIMAL_SCALE = 4;
const DECIMAL_PATTERN = /^\d{1,14}(\.\d{1,4})?$/;

/** แปลงสตริงทศนิยมเป็นจำนวนเต็ม scale คงที่ — คืน `null` เมื่อรูปแบบผิด
 * (ตรงกับ `isPositiveQuantity` ของ `lib/sales/salesView.ts`) */
function toScaledInt(value: string): bigint | null {
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) {
    return null;
  }
  const [intPart, fracPart = ''] = trimmed.split('.');
  const paddedFrac = fracPart.padEnd(DECIMAL_SCALE, '0');
  return BigInt(intPart) * 10n ** BigInt(DECIMAL_SCALE) + BigInt(paddedFrac);
}

function fromScaledInt(scaled: bigint): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const divisor = 10n ** BigInt(DECIMAL_SCALE);
  const intPart = magnitude / divisor;
  const fracPart = (magnitude % divisor).toString().padStart(DECIMAL_SCALE, '0');
  const trimmedFrac = fracPart.replace(/0+$/, '');
  const body = trimmedFrac === '' ? `${intPart}` : `${intPart}.${trimmedFrac}`;
  return negative && body !== '0' ? `-${body}` : body;
}

/**
 * จำนวนที่กรอกใช้ได้หรือยัง — เป็นสตริง ตรงกับรูปแบบ `NUMERIC(18,4)` และ `> 0`
 *
 * เหมือน `isPositiveQuantity` ของ `lib/sales/salesView.ts` ทุกประการ ประกาศแยก
 * ที่นี่ด้วยเหตุผลเดียวกับที่ทั้งไฟล์ไม่ import จาก `lib/sales/**`
 * (forbidden_scope ของ Work Order นี้)
 */
export function isPositivePackingQuantity(value: string): boolean {
  const trimmed = value.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) {
    return false;
  }
  return /[1-9]/.test(trimmed);
}

/** UUID สี่รูปแบบมาตรฐาน (ตัวพิมพ์เล็ก/ใหญ่ไม่สำคัญ) — ตรวจคร่าว ๆ ก่อนส่งไปให้
 * เซิร์ฟเวอร์ตัดสิน ไม่ใช่การจำลองการตรวจสอบฝั่งเซิร์ฟเวอร์ */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function looksLikeUuid(value: string): boolean {
  return UUID_PATTERN.test(value.trim());
}

/** สรุปผลรวมของ lot เทียบกับจำนวนของกล่อง — คำนวณด้วยจำนวนเต็ม scale คงที่
 * เท่านั้น ไม่แตะ `Number()`/`parseFloat` เลยแม้แต่ก้าวเดียว */
export interface PackingSumSummary {
  /** ผลรวมจำนวนของทุก lot ที่กรอกครบ (ข้ามแถวที่กรอกไม่ครบ/รูปแบบผิด) */
  readonly lotTotal: string;
  /** `packedQty - lotTotal` — บวกแปลว่ากล่องยังขาด ลบแปลว่าเกิน */
  readonly difference: string | null;
  /** `packedQty` ที่กรอกใช้ได้ และ `difference === 0` */
  readonly matches: boolean;
}

export function packingSumSummary(
  packedQty: string,
  lots: readonly { readonly packedQty: string }[],
): PackingSumSummary {
  let total = 0n;
  for (const lot of lots) {
    const scaled = toScaledInt(lot.packedQty);
    if (scaled !== null) {
      total += scaled;
    }
  }
  const lotTotal = fromScaledInt(total);

  const declared = toScaledInt(packedQty);
  if (declared === null) {
    return { lotTotal, difference: null, matches: false };
  }

  const difference = declared - total;
  return {
    lotTotal,
    difference: fromScaledInt(difference),
    matches: difference === 0n,
  };
}

/* ------------------------------------------------------------- สถานะ ----- */

export interface PackingUnitStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/** หกสถานะของ lifecycle ตามลำดับที่ `packing.py` ประกาศไว้ */
export const PACKING_UNIT_STATUS_DESCRIPTORS: readonly PackingUnitStatusDescriptor[] = [
  {
    code: DRAFT_PACKING_STATUS,
    label: 'ร่าง (DRAFT)',
    summary: 'เปิดไว้แต่ยังไม่มี lot — ไม่มี route ใดในหน้าจอนี้เขียนสถานะนี้',
  },
  {
    code: PACKING_PACKING_STATUS,
    label: 'กำลังแพ็ก (PACKING)',
    summary:
      'เปิดกล่องแล้วพร้อม lot — จำนวน lot ยังไม่ต้องเท่ากับจำนวนกล่องจนกว่าจะยืนยัน',
  },
  {
    code: PACKED_PACKING_STATUS,
    label: 'ปิดกล่องแล้ว (PACKED)',
    summary: 'ยืนยันแล้ว — ผลรวม lot เท่ากับจำนวนกล่องแล้วจริง แก้ไขต้อง "เปิดกล่องใหม่"',
  },
  {
    code: LOADED_PACKING_STATUS,
    label: 'ขึ้นรถแล้ว (LOADED)',
    summary: 'ขึ้นรถรอส่งแล้ว — เขียนโดยงาน Delivery (แยก Work Order)',
  },
  {
    code: DISPATCHED_PACKING_STATUS,
    label: 'ส่งออกแล้ว (DISPATCHED)',
    summary: 'ออกจากโรงงานไปแล้ว — เขียนโดยงาน Delivery, unpack ไม่ได้อีก',
  },
  {
    code: RETURNED_PACKING_STATUS,
    label: 'ถูกตีกลับ (RETURNED)',
    summary:
      'ส่งไปลูกค้าแล้วถูกตีกลับ — คุณภาพของยังไม่ถูกตัดสิน เปิดกล่องใหม่ไม่ได้ในเฟสนี้',
  },
];

export type PackingUnitStatusTone = 'neutral' | 'warning' | 'success' | 'info' | 'danger';

export function packingUnitStatusTone(status: string): PackingUnitStatusTone {
  switch (status) {
    case DRAFT_PACKING_STATUS:
    case PACKING_PACKING_STATUS:
      return 'warning';
    case PACKED_PACKING_STATUS:
    case DISPATCHED_PACKING_STATUS:
      return 'success';
    case LOADED_PACKING_STATUS:
      return 'info';
    case RETURNED_PACKING_STATUS:
      return 'danger';
    default:
      return 'neutral';
  }
}

export function packingUnitStatusLabel(status: string): string {
  return (
    PACKING_UNIT_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status
  );
}

/**
 * ใบในสถานะนี้มี transition อะไรให้กระทำได้บ้าง — อ่านตรงจาก
 * `CONFIRM_SOURCE_PACKING_STATUSES` / `UNPACK_SOURCE_PACKING_STATUSES` ของ
 * `service.py`: `confirm` รับเฉพาะ `PACKING` และ `unpack` รับเฉพาะ `PACKED`
 * เท่านั้น — ไม่ใช่ "ทุกสถานะยกเว้น X"
 *
 * `RETURNED` ไม่มีปุ่มใดเลย แม้ contract จะอธิบายเหตุผลการปฏิเสธไว้ยาว
 * (`RETURNED_PACKAGE_CANNOT_BE_UNPACKED`) เพราะเสนอปุ่มที่รู้ล่วงหน้าว่าจะถูก
 * ปฏิเสธเสมอไม่ใช่ deny-safe
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย
 */
export function packingUnitLifecycleActionsFor(
  status: string,
): readonly PackingUnitTransition[] {
  switch (status) {
    case PACKING_PACKING_STATUS:
      return ['confirm'];
    case PACKED_PACKING_STATUS:
      return ['unpack'];
    default:
      return [];
  }
}

/** สิทธิ์ที่ปุ่มแต่ละปุ่มต้องการ — ทั้งคู่เป็น `EDIT` ตรงกับ router จริง
 * (`require_permission(SALES_MODULE, "EDIT")` บนทั้ง `confirm` และ `unpack`) —
 * **ไม่ใช่ APPROVE** แม้ `unpack` จะ "เปิดกล่องที่ยืนยันแล้ว" ก็ตาม: APPROVE สงวน
 * ไว้ให้การกลับ Delivery ซึ่งไม่ได้อยู่บนหน้าจอนี้ */
export function packingUnitActionPermission(
  transition: PackingUnitTransition,
): 'EDIT' {
  switch (transition) {
    case 'confirm':
    case 'unpack':
      return 'EDIT';
  }
}

export function packingUnitActionLabel(transition: PackingUnitTransition): string {
  switch (transition) {
    case 'confirm':
      return 'ยืนยันปิดกล่อง';
    case 'unpack':
      return 'เปิดกล่องใหม่ (แก้ไข)';
  }
}

export function packingUnitActionTargetStatus(transition: PackingUnitTransition): string {
  switch (transition) {
    case 'confirm':
      return PACKED_PACKING_STATUS;
    case 'unpack':
      return PACKING_PACKING_STATUS;
  }
}

/* -------------------------------------------------------------- ฟอร์ม ----- */

export interface CreatePackingUnitFields {
  readonly plantCode: string;
  readonly soNumber: string;
  readonly lineNo: string;
  readonly productCode: string;
  readonly storageLocationCode: string;
  readonly packedQty: string;
}

export const EMPTY_CREATE_PACKING_UNIT_FIELDS: CreatePackingUnitFields = {
  plantCode: '',
  soNumber: '',
  lineNo: '',
  productCode: '',
  storageLocationCode: '',
  packedQty: '',
};

export interface PackingUnitLotDraft {
  readonly lotId: string;
  readonly packedQty: string;
}

export const EMPTY_PACKING_UNIT_LOT_DRAFT: PackingUnitLotDraft = {
  lotId: '',
  packedQty: '',
};

function isPositiveIntegerString(value: string): boolean {
  return /^[1-9]\d*$/.test(value.trim());
}

/**
 * ประกอบ body ของ `POST /v1/packing-units` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * เงื่อนไขที่ต้องครบ: ห้าช่องของหัวกล่องไม่ว่าง `line_no` เป็นจำนวนเต็มบวก
 * `packed_qty` ของกล่องเป็นทศนิยมบวกที่ถูกรูปแบบ **มีอย่างน้อยหนึ่ง lot** และทุก
 * lot มี `lot_id` ที่หน้าตาเป็น UUID กับ `packed_qty` บวกที่ถูกรูปแบบ — lot เดียว
 * ที่ผิดรูปทำให้ **ทั้งคำขอ** ประกอบไม่ได้ ไม่ใช่ถูกตัดทิ้งเงียบ ๆ เพราะ
 * `POST /v1/packing-units` เป็น all-or-nothing อยู่แล้วฝั่งเซิร์ฟเวอร์ (หนึ่ง
 * transaction เดียว)
 *
 * **ไม่ตรวจผลรวม lot เทียบกับ `packed_qty` ที่นี่** โดยตั้งใจ: กล่องเกิดที่
 * `PACKING` และจำนวน lot ไม่ต้องเท่ากับจำนวนกล่องจนกว่าจะ `confirm`
 * (`_resolve_packed_lots` ของ `service.py` บอกตรง ๆ ว่า "the sum rule is
 * deliberately not checked here") — ด่านของผลรวมคือปุ่มยืนยัน ไม่ใช่ปุ่มเปิดกล่อง
 */
export function toCreatePackingUnitBody(
  fields: CreatePackingUnitFields,
  lots: readonly PackingUnitLotDraft[],
): CreatePackingUnitBody | null {
  const plantCode = fields.plantCode.trim();
  const soNumber = fields.soNumber.trim();
  const lineNoRaw = fields.lineNo.trim();
  const productCode = fields.productCode.trim();
  const storageLocationCode = fields.storageLocationCode.trim();
  const packedQty = fields.packedQty.trim();

  if (
    plantCode === '' ||
    soNumber === '' ||
    productCode === '' ||
    storageLocationCode === '' ||
    !isPositiveIntegerString(lineNoRaw) ||
    !isPositivePackingQuantity(packedQty) ||
    lots.length === 0
  ) {
    return null;
  }

  const serializedLots: CreatePackingUnitLot[] = [];
  for (const lot of lots) {
    const lotId = lot.lotId.trim();
    const lotPackedQty = lot.packedQty.trim();
    if (!looksLikeUuid(lotId) || !isPositivePackingQuantity(lotPackedQty)) {
      return null;
    }
    serializedLots.push({ lot_id: lotId, packed_qty: lotPackedQty });
  }

  return {
    plant_code: plantCode,
    so_number: soNumber,
    line_no: Number(lineNoRaw),
    product_code: productCode,
    storage_location_code: storageLocationCode,
    packed_qty: packedQty,
    lots: serializedLots,
  };
}

/** ตัวกรองสถานะ — คืน `undefined` เมื่อ "ทุกสถานะ" (ไม่ส่ง `?status=` เลย) */
export function toPackingUnitStatusFilter(status: string): string | undefined {
  const trimmed = status.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** เลขใบสั่งขายที่กรอกไว้ในช่องกรอง — คืน `undefined` เมื่อว่าง (ไม่ส่ง
 * `?so_number=`) — เป็นตัวกรองจริงของเซิร์ฟเวอร์ ไม่ใช่ตัวกรองฝั่งเบราว์เซอร์
 * เพราะ `GET /v1/packing-units` ไม่มีคำค้นอิสระ */
export function toPackingUnitSoNumberFilter(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** `unpack` ต้องไม่ถูกเสนอกับกล่องที่ `RETURNED` หรือ `DISPATCHED` — ใช้ตรวจในเทสต์
 * โดยตรง (acceptance ข้อสาม) แม้ `packingUnitLifecycleActionsFor` จะครอบคลุมอยู่
 * แล้วก็ตาม เพราะเป็นกฎที่ acceptance ตั้งชื่อไว้ชัดเจน */
export function offersUnpack(status: string): boolean {
  return packingUnitLifecycleActionsFor(status).includes('unpack');
}

export type { PackingUnitSummary };
